import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  LogOut,
  Download,
  Search,
  Filter,
  RefreshCcw,
  Mail,
  Phone as PhoneIcon,
  Calendar,
  MessageSquare,
  X as XIcon,
  StickyNote,
  Save,
} from "lucide-react";

const LOGO =
  "https://customer-assets.emergentagent.com/job_tank-showcase/artifacts/dfuf65tx_exact%20sized%20transaprent%20file.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_OPTIONS = [
  "Water Tanks",
  "Agri Tanks",
  "Industrial Liquid Storage",
  "Borehole Drilling",
  "Custom / Other",
];

const STATUSES = [
  { value: "new", label: "New", bg: "#EBE7DE", fg: "#4A4E58", dot: "#8B8373" },
  { value: "contacted", label: "Contacted", bg: "#E8EEF6", fg: "#1A2F4C", dot: "#1A2F4C" },
  { value: "quoted", label: "Quoted", bg: "#F7ECD2", fg: "#7A5A12", dot: "#B8851F" },
  { value: "won", label: "Won", bg: "#DDEDE1", fg: "#2F6B3F", dot: "#2F6B3F" },
  { value: "lost", label: "Lost", bg: "#F4DCDF", fg: "#8B1E28", dot: "#8B1E28" },
];

const statusMeta = (value) =>
  STATUSES.find((s) => s.value === value) || STATUSES[0];

const authHeaders = () => {
  const t = localStorage.getItem("mutumwa_admin_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    product_interest: "",
    status: "",
    start_date: "",
    end_date: "",
    q: "",
  });
  const [selected, setSelected] = useState(null);
  const [editStatus, setEditStatus] = useState("new");
  const [editNotes, setEditNotes] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem("mutumwa_admin_token");
    localStorage.removeItem("mutumwa_admin_expires");
    nav("/admin/login", { replace: true });
  }, [nav]);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.product_interest) params.product_interest = filters.product_interest;
      if (filters.status) params.status = filters.status;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.q) params.q = filters.q;
      const [lRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/contacts`, { headers: authHeaders(), params }),
        axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
      ]);
      setLeads(lRes.data);
      setStats(sRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        logout();
      } else {
        toast.error("Failed to load leads.");
      }
    } finally {
      setLoading(false);
    }
  }, [filters, logout]);

  useEffect(() => {
    loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = (e) => {
    e.preventDefault();
    loadLeads();
  };

  const clearFilters = () => {
    const empty = { product_interest: "", status: "", start_date: "", end_date: "", q: "" };
    setFilters(empty);
    // reload with empty filters directly to avoid stale-closure issue
    (async () => {
      setLoading(true);
      try {
        const [lRes, sRes] = await Promise.all([
          axios.get(`${API}/admin/contacts`, { headers: authHeaders() }),
          axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
        ]);
        setLeads(lRes.data);
        setStats(sRes.data);
      } catch (err) {
        if (err?.response?.status === 401) {
          toast.error("Session expired. Please sign in again.");
          logout();
        } else {
          toast.error("Failed to load leads.");
        }
      } finally {
        setLoading(false);
      }
    })();
  };

  const openLead = (lead) => {
    setSelected(lead);
    setEditStatus(lead.status || "new");
    setEditNotes(lead.notes || "");
  };

  const saveLeadEdits = async () => {
    if (!selected) return;
    setSavingEdit(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/contacts/${selected.id}`,
        { status: editStatus, notes: editNotes },
        { headers: authHeaders() }
      );
      setLeads((prev) => prev.map((l) => (l.id === data.id ? data : l)));
      setSelected(data);
      toast.success("Lead updated.");
      // refresh stats in background
      axios
        .get(`${API}/admin/stats`, { headers: authHeaders() })
        .then((r) => setStats(r.data))
        .catch(() => {});
    } catch (err) {
      const msg = err?.response?.data?.detail || "Update failed.";
      toast.error(typeof msg === "string" ? msg : "Update failed.");
    } finally {
      setSavingEdit(false);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/contacts.csv`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `mutumwa-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch {
      toast.error("Export failed.");
    }
  };

  const productBadges = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.by_product).sort((a, b) => b[1] - a[1]);
  }, [stats]);

  return (
    <div className="min-h-screen bg-[#F7F5F0] grain-overlay">
      <Toaster position="top-right" richColors />

      {/* Top bar */}
      <header
        data-testid="admin-header"
        className="sticky top-0 z-40 bg-[#F7F5F0]/90 backdrop-blur-md border-b border-[rgba(26,47,76,0.12)]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="Mutumwa" className="h-10 w-auto" />
            <div className="hidden md:block">
              <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#8B1E28]">
                Admin
              </div>
              <div className="font-display italic text-[#1A2F4C] text-lg leading-none">
                Lead Console
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadLeads}
              data-testid="admin-refresh-btn"
              className="p-2 text-[#1A2F4C] hover:text-[#8B1E28]"
              title="Refresh"
            >
              <RefreshCcw size={18} />
            </button>
            <button
              onClick={exportCsv}
              data-testid="admin-export-btn"
              className="btn-outline !py-3 !px-5 text-sm"
            >
              <Download size={15} /> Export CSV
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="btn-primary !py-3 !px-5 text-sm"
            >
              <LogOut size={15} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        {/* Title */}
        <div className="mb-10">
          <span className="text-xs uppercase tracking-[0.24em] font-bold text-[#8B1E28]">
            / Dashboard
          </span>
          <h1 className="font-display font-black text-4xl md:text-5xl text-[#1A2F4C] tracking-tight leading-[0.95] mt-3">
            All enquiries in <em className="italic font-light">one place.</em>
          </h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-10">
          <StatCard
            label="Total enquiries"
            value={stats?.total ?? "—"}
            testid="stat-total"
          />
          <StatCard
            label="This week"
            value={stats?.this_week ?? "—"}
            testid="stat-week"
          />
          <div
            data-testid="stat-by-status"
            className="bg-white border border-[rgba(26,47,76,0.15)] p-6"
          >
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#8B1E28] mb-3">
              By status
            </div>
            {!stats ? (
              <div className="text-[#4A4E58] text-sm">No data yet.</div>
            ) : (
              <ul className="space-y-1.5">
                {STATUSES.map((s) => (
                  <li key={s.value} className="flex justify-between items-center text-sm">
                    <span className="flex items-center gap-2 text-[#1A2F4C]">
                      <span className="inline-block w-2 h-2 rounded-full" style={{ background: s.dot }} />
                      {s.label}
                    </span>
                    <span className="font-display text-[#1A2F4C] font-bold">
                      {stats?.by_status?.[s.value] ?? 0}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div
            data-testid="stat-by-product"
            className="bg-white border border-[rgba(26,47,76,0.15)] p-6"
          >
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#8B1E28] mb-3">
              By product
            </div>
            {productBadges.length === 0 ? (
              <div className="text-[#4A4E58] text-sm">No data yet.</div>
            ) : (
              <ul className="space-y-1.5">
                {productBadges.map(([k, v]) => (
                  <li key={k} className="flex justify-between items-center text-sm">
                    <span className="text-[#1A2F4C]">{k}</span>
                    <span className="font-display text-[#1A2F4C] font-bold">{v}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Filters */}
        <form
          onSubmit={applyFilters}
          data-testid="admin-filters"
          className="bg-white border border-[rgba(26,47,76,0.15)] p-6 mb-8"
        >
          <div className="flex items-center gap-2 mb-5 text-xs uppercase tracking-[0.24em] font-bold text-[#1A2F4C]">
            <Filter size={14} /> Filters
          </div>
          <div className="grid grid-cols-1 md:grid-cols-14 gap-4" style={{ gridTemplateColumns: "repeat(14, minmax(0, 1fr))" }}>
            <div className="md:col-span-4 flex items-center border-b-2 border-[rgba(26,47,76,0.2)] focus-within:border-[#8B1E28] transition-colors">
              <Search size={15} className="text-[#4A4E58]" />
              <input
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="Search name, email, phone or message"
                data-testid="filter-search"
                className="input-editorial !border-0 !py-2"
              />
            </div>
            <div className="md:col-span-3">
              <select
                value={filters.product_interest}
                onChange={(e) =>
                  setFilters({ ...filters, product_interest: e.target.value })
                }
                data-testid="filter-product"
                className="input-editorial !py-2"
              >
                <option value="">All products</option>
                {PRODUCT_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                data-testid="filter-status"
                className="input-editorial !py-2"
              >
                <option value="">All statuses</option>
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                data-testid="filter-start-date"
                className="input-editorial !py-2"
              />
            </div>
            <div className="md:col-span-2">
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                data-testid="filter-end-date"
                className="input-editorial !py-2"
              />
            </div>
            <div className="md:col-span-1 flex gap-2">
              <button
                type="submit"
                data-testid="filter-apply"
                className="btn-primary !py-3 !px-4 text-xs w-full justify-center"
              >
                Apply
              </button>
            </div>
          </div>
          <div className="flex justify-end mt-3">
            <button
              type="button"
              onClick={clearFilters}
              data-testid="filter-clear"
              className="text-xs uppercase tracking-[0.18em] font-bold text-[#4A4E58] hover:text-[#8B1E28]"
            >
              Clear filters
            </button>
          </div>
        </form>

        {/* Leads table */}
        <div
          data-testid="leads-table-container"
          className="bg-white border border-[rgba(26,47,76,0.15)]"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#EBE7DE]">
                <tr>
                  {["Received", "Name", "Contact", "Product", "Status", "Message", ""].map((h) => (
                    <th
                      key={h}
                      className="text-left text-[10px] uppercase tracking-[0.2em] font-bold text-[#1A2F4C] px-5 py-4 whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-10 text-center text-[#4A4E58]">
                      Loading leads…
                    </td>
                  </tr>
                ) : leads.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      data-testid="leads-empty"
                      className="px-5 py-16 text-center text-[#4A4E58]"
                    >
                      No enquiries match your filters yet.
                    </td>
                  </tr>
                ) : (
                  leads.map((l) => {
                    const sm = statusMeta(l.status || "new");
                    return (
                      <tr
                        key={l.id}
                        data-testid={`lead-row-${l.id}`}
                        className="border-t border-[rgba(26,47,76,0.08)] hover:bg-[#F7F5F0] transition-colors cursor-pointer"
                        onClick={() => openLead(l)}
                      >
                        <td className="px-5 py-4 text-sm text-[#4A4E58] whitespace-nowrap align-top">
                          {formatDate(l.created_at)}
                        </td>
                        <td className="px-5 py-4 text-sm text-[#1A2F4C] font-semibold align-top">
                          {l.name}
                        </td>
                        <td className="px-5 py-4 text-sm align-top">
                          <div className="flex items-center gap-2 text-[#1A2F4C]">
                            <Mail size={13} className="text-[#8B1E28]" /> {l.email}
                          </div>
                          <div className="flex items-center gap-2 text-[#4A4E58] mt-1">
                            <PhoneIcon size={13} /> {l.phone}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-[#1A2F4C] align-top whitespace-nowrap">
                          {l.product_interest ? (
                            <span className="inline-block px-2.5 py-1 bg-[#EBE7DE] text-xs uppercase tracking-wider font-bold">
                              {l.product_interest}
                            </span>
                          ) : (
                            <span className="text-[#4A4E58] text-xs uppercase tracking-wider">—</span>
                          )}
                        </td>
                        <td
                          data-testid={`lead-status-${l.id}`}
                          className="px-5 py-4 text-sm align-top whitespace-nowrap"
                        >
                          <span
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold"
                            style={{ background: sm.bg, color: sm.fg }}
                          >
                            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: sm.dot }} />
                            {sm.label}
                          </span>
                          {l.notes && (
                            <span
                              title="Has notes"
                              className="ml-2 inline-block align-middle text-[#8B1E28]"
                            >
                              •
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-4 text-sm text-[#2C303A] align-top max-w-md">
                          <div className="line-clamp-2">{l.message}</div>
                        </td>
                        <td className="px-5 py-4 text-right align-top">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openLead(l);
                            }}
                            data-testid={`lead-view-${l.id}`}
                            className="text-xs uppercase tracking-[0.18em] font-bold text-[#8B1E28] hover:text-[#1A2F4C]"
                          >
                            Open
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 text-xs text-[#4A4E58] flex justify-between">
          <span>{leads.length} lead{leads.length === 1 ? "" : "s"} shown</span>
          <span>Showing most recent first · Max 200</span>
        </div>
      </main>

      {/* Detail drawer */}
      {selected && (
        <div
          data-testid="lead-detail-overlay"
          className="fixed inset-0 z-50 bg-[#1A2F4C]/40 backdrop-blur-sm flex items-stretch justify-end"
          onClick={() => setSelected(null)}
        >
          <div
            data-testid="lead-detail-panel"
            className="w-full max-w-lg bg-[#F7F5F0] h-full overflow-y-auto p-8 md:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-8">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#8B1E28]">
                  Enquiry
                </div>
                <h2 className="font-display font-black text-3xl text-[#1A2F4C] mt-2 leading-tight">
                  {selected.name}
                </h2>
              </div>
              <button
                onClick={() => setSelected(null)}
                data-testid="lead-detail-close"
                className="text-[#1A2F4C] hover:text-[#8B1E28]"
              >
                <XIcon size={20} />
              </button>
            </div>
            <dl className="space-y-5">
              <Row icon={<Calendar size={14} />} label="Received" value={formatDate(selected.created_at)} />
              <Row
                icon={<Mail size={14} />}
                label="Email"
                value={<a className="link-underline" href={`mailto:${selected.email}`}>{selected.email}</a>}
              />
              <Row
                icon={<PhoneIcon size={14} />}
                label="Phone"
                value={<a className="link-underline" href={`tel:${selected.phone}`}>{selected.phone}</a>}
              />
              <Row
                icon={<Filter size={14} />}
                label="Product interest"
                value={selected.product_interest || "Unspecified"}
              />
              <Row
                icon={<Filter size={14} />}
                label="Quantity"
                value={selected.quantity != null ? `${selected.quantity} tank${selected.quantity === 1 ? "" : "s"}` : "Not specified"}
              />
              {selected.updated_at && (
                <Row
                  icon={<Calendar size={14} />}
                  label="Last updated"
                  value={formatDate(selected.updated_at)}
                />
              )}
            </dl>

            {/* Status + Notes editor */}
            <div className="mt-8 bg-white border border-[rgba(26,47,76,0.15)] p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.24em] font-bold text-[#8B1E28] mb-4">
                <StickyNote size={14} /> Pipeline
              </div>
              <div className="mb-5">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#4A4E58] mb-2">
                  Status
                </div>
                <div className="flex flex-wrap gap-2" data-testid="lead-status-picker">
                  {STATUSES.map((s) => {
                    const active = editStatus === s.value;
                    return (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => setEditStatus(s.value)}
                        data-testid={`status-option-${s.value}`}
                        className="px-3 py-2 text-[10px] uppercase tracking-wider font-bold transition-all"
                        style={{
                          background: active ? s.dot : s.bg,
                          color: active ? "#F7F5F0" : s.fg,
                          borderRadius: 0,
                        }}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#4A4E58] mb-2">
                  Internal notes
                </div>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Follow-up details, quote reference, next steps..."
                  rows={4}
                  data-testid="lead-notes-textarea"
                  className="w-full bg-[#F7F5F0] border border-[rgba(26,47,76,0.15)] p-3 text-sm text-[#1A2F4C] outline-none focus:border-[#8B1E28] transition-colors resize-none"
                />
              </div>
              <button
                onClick={saveLeadEdits}
                disabled={
                  savingEdit ||
                  (editStatus === (selected.status || "new") &&
                    editNotes === (selected.notes || ""))
                }
                data-testid="lead-save-btn"
                className="btn-primary mt-4 w-full justify-center text-sm disabled:opacity-50"
              >
                <Save size={14} /> {savingEdit ? "Saving..." : "Save changes"}
              </button>
            </div>

            <div className="mt-8">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.24em] font-bold text-[#8B1E28] mb-3">
                <MessageSquare size={14} /> Customer message
              </div>
              <div className="bg-white border border-[rgba(26,47,76,0.15)] p-5 text-[#1A2F4C] whitespace-pre-wrap leading-relaxed">
                {selected.message}
              </div>
            </div>
            <div className="mt-8 flex gap-3">
              <a href={`mailto:${selected.email}`} className="btn-primary flex-1 justify-center">
                Reply by email
              </a>
              <a
                href={`https://wa.me/${selected.phone.replace(/[^\d]/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline flex-1 justify-center"
              >
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const StatCard = ({ label, value, testid }) => (
  <div
    data-testid={testid}
    className="bg-white border border-[rgba(26,47,76,0.15)] p-6"
  >
    <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#8B1E28]">
      {label}
    </div>
    <div className="font-display font-black text-[#1A2F4C] text-5xl mt-2 leading-none">
      {value}
    </div>
  </div>
);

const Row = ({ icon, label, value }) => (
  <div className="flex gap-5 items-start">
    <div className="w-32 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#4A4E58] mt-1">
      {icon} {label}
    </div>
    <div className="flex-1 text-[#1A2F4C] text-sm break-words">{value}</div>
  </div>
);
